// CLOCKWORK B2 (2026-10-31): every digital employee (DE) and agent (AG) carries a role, a goal and a
// backstory, in every company. Role says what it is, goal what it must achieve and how a person
// judges it, backstory who it is as a practitioner: the standard it holds, how it thinks, what it
// does when stuck and what it never does (five short paragraphs, 400 to 900 characters).
//
// One module for the API, the MCP control tools and the web forms: the required fields, the floor
// on a backstory, the check, and the platform default a record gets when it is created without
// one (and that migration 0204 wrote into existing records that had none). Pure: zod-free, no I/O.

/** The three fields no DE or AG may be without. Standing orders stay optional. */
export const REQUIRED_PROFILE_FIELDS = ["role", "goal", "backstory"] as const;
export type RequiredProfileField = (typeof REQUIRED_PROFILE_FIELDS)[number];

/** A backstory shorter than this is a label, not a backstory. */
export const BACKSTORY_MIN_CHARS = 80;
/** The column caps (`*_profile_text_check`). */
export const PROFILE_MAX_CHARS = { role: 2000, goal: 4000, backstory: 8000 } as const;
/** The shape the Add-DE and Add-AG forms and the control tools describe to the person. */
export const BACKSTORY_SHAPE_HINT =
  "Five short paragraphs, 400 to 900 characters: who you are (the top practitioner of this craft), the standard you hold, how you think (reading, options, choice), what you do when stuck (fix it, re-plan, then PROPOSAL or BLOCKED with facts), what you never do.";

export type ProfileTexts = {
  role?: string | null | undefined;
  goal?: string | null | undefined;
  backstory?: string | null | undefined;
};

const blank = (s: string | null | undefined) => !s || s.trim() === "";

/** The required fields that are missing, blank, or (backstory) too short; empty when complete. */
export function profileGaps(p: ProfileTexts): RequiredProfileField[] {
  return REQUIRED_PROFILE_FIELDS.filter((f) => {
    const v = p[f];
    if (blank(v)) return true;
    return f === "backstory" && (v as string).trim().length < BACKSTORY_MIN_CHARS;
  });
}

/** Human wording for a gap list ("role and backstory"), for an error or a form hint. */
export function profileGapText(gaps: readonly RequiredProfileField[]): string {
  return gaps.length > 1
    ? `${gaps.slice(0, -1).join(", ")} and ${gaps[gaps.length - 1]}`
    : (gaps[0] ?? "");
}

/**
 * The platform defaults. `{name}` is the DE's or AG's name. The SQL of migration 0204 holds the
 * same sentences (a test keeps the two together).
 */
export const PROFILE_DEFAULT_TEMPLATES = {
  role: "{name}: owns the work it is given, from the first read of the request to a checked result.",
  goal: "Deliver each piece of work to the standard its requester would call great the first time. Check the result against what was asked and report any gap or failure the same hour, with the facts.",
  backstory: [
    "You are {name}, the practitioner in this company who does this craft at the level a specialist would be proud of, and you do whatever it takes to deliver a great result.",
    "You hold one standard: great, not done. Great is what the requester asked for and what they meant, checked before you hand it on.",
    "You think before you make. Restate the goal in your own words, weigh two or three ways to serve it, choose one and say why.",
    "When you are stuck, fix it yourself, then try another approach. When the brief is thin or wrong, answer PROPOSAL with the better idea and its cost. When you cannot go on, say BLOCKED with the facts, what you tried and your options.",
    "You never guess a fact, never skip the check and never leave a request without an answer.",
  ].join("\n\n"),
} as const;

const fill = (template: string, name: string) => template.replaceAll("{name}", name.trim());

/** The default role, goal and backstory for a worker called `name`. */
export function defaultProfile(name: string): Record<RequiredProfileField, string> {
  const n = name.trim() || "This worker";
  return {
    role: fill(PROFILE_DEFAULT_TEMPLATES.role, n),
    goal: fill(PROFILE_DEFAULT_TEMPLATES.goal, n),
    backstory: fill(PROFILE_DEFAULT_TEMPLATES.backstory, n),
  };
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const DEFAULT_RES = Object.fromEntries(
  REQUIRED_PROFILE_FIELDS.map((f) => [
    f,
    new RegExp(`^${PROFILE_DEFAULT_TEMPLATES[f].split("{name}").map(escapeRe).join("[^\\n]+?")}$`),
  ]),
) as Record<RequiredProfileField, RegExp>;

/**
 * True when `text` is the platform default for `field` (for any name): such text was never written
 * by a person, so a profile sync may replace it with the shipped profile.
 */
export function isPlatformDefault(field: RequiredProfileField, text: string | null | undefined) {
  return !!text && DEFAULT_RES[field].test(text.trim());
}

/**
 * `p` with every required field that is blank filled from the platform default (a field already
 * written is kept as it is, even when short: the zod contract refuses a short one earlier).
 */
export function withProfileDefaults<T extends ProfileTexts>(p: T, name: string): T {
  const d = defaultProfile(name);
  const out = { ...p };
  for (const f of REQUIRED_PROFILE_FIELDS) if (blank(out[f])) (out as ProfileTexts)[f] = d[f];
  return out;
}
